import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import {
  ApiPaginationResult,
  ApiResult,
} from '../../shared/models/api-result.model';
import {
  ContractStatus,
  ContractStatusTag,
  EligibilityRuleAvaluationStatus,
  InstallmentStatus,
  InvoiceStatus,
  OrderContract,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  ShipmentStatus,
} from '../models/order.model';
import {
  CancelOrderRequest,
  CreateOrderShipmentRequest,
  CreateProductIdentifiersRequest,
  UpdateProductIdentifiersRequest,
} from '../pages/order/order-details/models/order-details.model';
import { SelectOption } from '../../shared/models/dropdowm.model';
import { map } from 'rxjs';
import { mapOrder, mapOrderList } from '../models/order.mapper';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private http = inject(HttpClient);

  apiBaseUrl = environment.apiBaseUrl;

  getAll(
    pageNumber: number,
    pageSize: number,
    number?: string,
    fullName?: string,
    productName?: string,
    status?: OrderStatus,
  ) {
    let params = '';

    if (number) params += `&number=${number}`;
    if (fullName) params += `&fullName=${fullName}`;
    if (productName) params += `&productName=${productName}`;
    if (status) params += `&status=${status}`;

    return this.http
      .get<ApiPaginationResult>(
        this.apiBaseUrl +
          `/api/v1/orders?pageNumber=${pageNumber}&pageSize=${pageSize}${params}`,
      )
      .pipe(
        map((response) =>
          response.data
            ? {
                ...response,
                data: {
                  ...response.data,
                  items: response.data.items?.map(mapOrderList),
                },
              }
            : response,
        ),
      );
  }

  getById(id: string) {
    return this.http
      .get<ApiResult>(this.apiBaseUrl + '/api/v1/orders/' + id)
      .pipe(
        map((response) =>
          response.data
            ? { ...response, data: mapOrder(response.data) }
            : response,
        ),
      );
  }

  updateEligibilityAssessmentRule(
    eligibilityAssessmentId: string,
    ruleEvaluationId: string,
    status: EligibilityRuleAvaluationStatus,
    note?: string,
  ) {
    return this.http.patch<ApiResult>(
      `${this.apiBaseUrl}/api/v1/eligibility-assessments/${eligibilityAssessmentId}/rule-evaluations/${ruleEvaluationId}`,
      {
        status,
        note,
      },
    );
  }

  completeEligibilityAssessment(
    eligibilityAssessmentId: string,
    note?: string,
  ) {
    return this.http.patch<ApiResult>(
      `${this.apiBaseUrl}/api/v1/eligibility-assessments/${eligibilityAssessmentId}/complete`,
      {
        note,
      },
    );
  }

  createProductIdentifiers(
    orderId: string,
    identifiers: CreateProductIdentifiersRequest[],
  ) {
    return this.http.post<ApiResult>(
      `${this.apiBaseUrl}/api/v1/orders/${orderId}/product-identifiers`,
      identifiers,
    );
  }

  updateProductIdentifiers(
    orderId: string,
    identifiers: UpdateProductIdentifiersRequest[],
  ) {
    return this.http.put<ApiResult>(
      `${this.apiBaseUrl}/api/v1/orders/${orderId}/product-identifiers`,
      identifiers,
    );
  }

  createOrderShipment(orderId: string, data: CreateOrderShipmentRequest) {
    return this.http.post<ApiResult>(
      `${this.apiBaseUrl}/api/v1/orders/${orderId}/shipments`,
      data,
    );
  }

  updateStatusOrderShipment(
    orderId: string,
    shipmentId: string,
    status: ShipmentStatus,
  ) {
    return this.http.patch<ApiResult>(
      `${this.apiBaseUrl}/api/v1/orders/${orderId}/shipments/${shipmentId}/status`,
      { status },
    );
  }

  payInstallment(installmentId: string) {
    return this.http.post<ApiResult>(
      `${this.apiBaseUrl}/api/v1/orders/installments/${installmentId}/pay`,
      null,
    );
  }

  sendContractReminder(orderId: string) {
    return this.http.post<ApiResult>(
      `${this.apiBaseUrl}/api/v1/orders/${orderId}/contracts/send-reminder`,
      null,
    );
  }

  cancel(orderId: string, body: CancelOrderRequest) {
    return this.http.post<ApiResult>(
      `${this.apiBaseUrl}/api/v1/orders/${orderId}/cancel`,
      body,
    );
  }

  getStatusTag(status: OrderStatus): string {
    return OrderStatus[status];
  }

  getStatusDescription(status: OrderStatus): string {
    switch (status) {
      case OrderStatus.DocumentsPending:
        return 'Aguardando documentos';

      case OrderStatus.DocumentsSubmitted:
        return 'Documentos enviados';

      case OrderStatus.UnderReview:
        return 'Em análise';

      case OrderStatus.Rejected:
        return 'Rejeitado';

      case OrderStatus.PaymentPending:
        return 'Aguardando pagamento';

      case OrderStatus.ContractPending:
        return 'Aguardando assinatura contrato';

      case OrderStatus.Confirmed:
        return 'Confirmado';

      case OrderStatus.Preparing:
        return 'Em preparação';

      case OrderStatus.Shipped:
        return 'Enviado';

      case OrderStatus.Delivered:
        return 'Entregue';

      case OrderStatus.Cancelled:
        return 'Cancelado';

      case OrderStatus.Reserved:
        return 'Produto reservado';

      default:
        return 'Status desconhecido';
    }
  }

  getOrderStatusOptions(): SelectOption[] {
    return [
      { label: 'Aguardando documentos', value: OrderStatus.DocumentsPending },
      { label: 'Documentos enviados', value: OrderStatus.DocumentsSubmitted },
      { label: 'Em análise', value: OrderStatus.UnderReview },
      { label: 'Rejeitado', value: OrderStatus.Rejected },
      {
        label: 'Aguardando assinatura contrato',
        value: OrderStatus.ContractPending,
      },
      { label: 'Aguardando pagamento', value: OrderStatus.PaymentPending },
      { label: 'Produto reservado', value: OrderStatus.Reserved },
      { label: 'Confirmado', value: OrderStatus.Confirmed },
      { label: 'Em preparação', value: OrderStatus.Preparing },
      { label: 'Enviado', value: OrderStatus.Shipped },
      { label: 'Entregue', value: OrderStatus.Delivered },
      { label: 'Cancelado', value: OrderStatus.Cancelled },
    ];
  }

  getShipmentStatusDescription(status: ShipmentStatus): string {
    switch (status) {
      case ShipmentStatus.AwaitingPickup:
        return 'Aguardando coleta';
      case ShipmentStatus.PickedUp:
        return 'Enviado';
      case ShipmentStatus.InTransit:
        return 'Em trânsito';
      case ShipmentStatus.OutForDelivery:
        return 'Saiu para entrega';
      case ShipmentStatus.Delivered:
        return 'Entregue';
      case ShipmentStatus.DeliveryException:
        return 'Ocorreu um problema na entrega';
      case ShipmentStatus.ReturnedToSender:
        return 'Devolvido';
      default:
        return 'Status desconhecido';
    }
  }

  getShipmentStatusDropdownOptions(): SelectOption[] {
    return [
      {
        label: 'Aguardando coleta',
        value: ShipmentStatus.AwaitingPickup,
      },
      {
        label: 'Enviado',
        value: ShipmentStatus.PickedUp,
      },
      {
        label: 'Em trânsito',
        value: ShipmentStatus.InTransit,
      },
      {
        label: 'Saiu para entrega',
        value: ShipmentStatus.OutForDelivery,
      },
      {
        label: 'Entregue',
        value: ShipmentStatus.Delivered,
      },
      {
        label: 'Ocorreu um problema na entrega',
        value: ShipmentStatus.DeliveryException,
      },
      {
        label: 'Devolvido',
        value: ShipmentStatus.ReturnedToSender,
      },
    ];
  }

  getInstallmentStatusDescription(status: InstallmentStatus): string {
    switch (status) {
      case InstallmentStatus.Pending:
        return 'À vencer';
      case InstallmentStatus.Paid:
        return 'Pago';
      case InstallmentStatus.PartiallyPaid:
        return 'Pago parcialmente';
      case InstallmentStatus.Overdue:
        return 'Em atraso';
      case InstallmentStatus.Cancelled:
        return 'Cancelado';
      case InstallmentStatus.Suspended:
        return 'Suspenso';
      case InstallmentStatus.Refunded:
        return 'Estornado';
      case InstallmentStatus.PendingRefund:
        return 'Pendente Estorno';
      default:
        return 'Status desconhecido';
    }
  }

  getInstallmentStatusComponent(status: InstallmentStatus): string {
    switch (status) {
      case InstallmentStatus.Paid:
        return `
         <div class="w-fit px-3 py-1 font-bold text-sm border-1 border-primary-dark rounded-2xl flex items-center gap-1 bg-primary-light">
          <i class="ph ph-check-circle text-lg text-primary-dark"></i>
          Pago
        </div>
        `;
      case InstallmentStatus.PartiallyPaid:
        return `
         <div class="w-fit px-3 py-1 font-bold text-sm border-1 border-primary-dark rounded-2xl flex items-center gap-1 bg-primary-light">
          <i class="ph ph-check-circle text-lg text-primary-dark"></i>
          Pago parcialmente
        </div>
        `;
      case InstallmentStatus.Overdue:
        return `
         <div class="w-fit px-3 py-1 font-bold text-sm border-1 border-warning-dark rounded-2xl flex items-center gap-1 bg-warning-light"> 
          <i class="ph ph-warning text-lg text-warning-dark"></i>
          Em atraso
        </div>
        `;
      case InstallmentStatus.Pending:
        return `
         <div class="w-fit px-3 py-1 font-bold text-sm border-1 border-info-light rounded-2xl flex items-center gap-1 bg-info-lightest">
          <i class="ph ph-info text-lg text-info-light"></i>
          À vencer
        </div>
        `;
      case InstallmentStatus.Cancelled:
        return `
         <div class="w-fit px-3 py-1 font-bold text-sm border-1 border-danger-dark rounded-2xl flex items-center gap-1 bg-danger-light">
          <i class="ph ph-x-circle text-lg text-danger-dark"></i>
          Cancelado
        </div>
        `;
      case InstallmentStatus.Suspended:
        return `
         <div class="w-fit px-3 py-1 font-bold text-sm border-1 border-warning-dark rounded-2xl flex items-center gap-1 bg-warning-light">
            <i class="ph ph-warning text-lg text-warning-dark"></i>
            Suspenso
          </div>
        `;
      case InstallmentStatus.Refunded:
        return `
          <div class="w-fit px-3 py-1 font-bold text-sm border-1 border-info-light rounded-2xl flex items-center gap-1 bg-info-lightest">
            <i class="ph ph-info text-lg text-info-light"></i>
            Estornado
          </div>
        `;
      case InstallmentStatus.PendingRefund:
        return `
         <div class="w-fit px-3 py-1 font-bold text-sm border-1 border-info-light rounded-2xl flex items-center gap-1 bg-info-lightest">
          <i class="ph ph-info text-lg text-info-light"></i>
          Pendente Estorno
        </div>
        `;
      default:
        return '';
    }
  }

  getPaymentStatusDescription(status: PaymentStatus): string {
    switch (status) {
      case PaymentStatus.Pending:
        return 'Pendente';
      case PaymentStatus.Processing:
        return 'Processando';
      case PaymentStatus.Confirmed:
        return 'Confirmado';
      case PaymentStatus.Failed:
        return 'Falhou';
      case PaymentStatus.Refunded:
        return 'Estornado';
      case PaymentStatus.Cancelled:
        return 'Cancelado';
      default:
        return 'Status desconhecido';
    }
  }

  getPaymentMethodDescription(status: PaymentMethod): string {
    switch (status) {
      case PaymentMethod.CreditCard:
        return 'Cartão de Crédito';
      case PaymentMethod.DebitCard:
        return 'Cartão de Débito';
      case PaymentMethod.BankSlip:
        return 'Boleto Bancário';
      case PaymentMethod.BankTransfer:
        return 'Transferência Bancária';
      default:
        return 'Status desconhecido';
    }
  }

  isContractSigned(contract?: OrderContract | null): boolean {
    return contract?.status === ContractStatus.Signed;
  }

  getContractStatusTag(contract?: OrderContract | null): ContractStatusTag {
    switch (contract?.status) {
      case ContractStatus.Signed:
        return { status: 'Approved', text: 'Assinado' };
      case ContractStatus.Cancelled:
        return { status: 'Cancelled', text: 'Cancelado' };
      case ContractStatus.Declined:
        return { status: 'Rejected', text: 'Recusado' };
      case ContractStatus.Generated:
      case ContractStatus.SentForSignature:
      default:
        return { status: 'Pending', text: 'Pendente assinatura' };
    }
  }

  getInvoiceStatusDescription(status: InvoiceStatus): string {
    switch (status) {
      case InvoiceStatus.Pending:
        return 'Pendente';
      case InvoiceStatus.Created:
        return 'Criado';
      case InvoiceStatus.Sent:
        return 'Enviado';
      default:
        return 'Status desconhecido';
    }
  }
}
